import { useEffect } from 'react';
import type { ShouldRevalidateFunctionArgs } from 'react-router';

import { apiIsomorphicFetchSynonymSearchApiKey } from '#/api/apiClientFetchSynonymSearchApiKey';
import { apiDeleteTmpStorage } from '#/api/tmpStorage/apiDeleteTmpStorage';
import { apiIsomorphicFetchUserEnvironment } from '#/api/userEnvironment/apiFetchUserEnvironment';
import { useAppConfig } from '#/config/AppConfigContext';
import { useQueryParamState } from '#/hooks/useQueryParamState';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { RouteModule } from '#/horizon/components/RouteModule';
import type { LoaderDataWithOverrideLocale } from '#/horizon/useMatchesOverrideLocale';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { salaryApiIsomorphicFetchRecentSubmissions } from '#/modules/salary/api/salaryApiFetchRecentSubmissions';
import { salaryApiIsomorphicFetchCompensationSurvey } from '#/modules/salary/api/salaryApiGetCompensationSurvey';
import { salaryApiIsomorphicFetchTmpStorage } from '#/modules/salary/api/tmpStorage/salaryApiFetchTmpStorage';
import { salariesExploreRoute } from '#/modules/salary/routing/salary.routes';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getAppRouteSearchParams } from '#/routing/utils/getAppRouteSearchParams';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SalariesExplorePageAuth } from './Auth/SalariesExplorePageAuth';
import { SalaryExplorePage } from './SalaryExplorePage';

export const handle = {
  noAds: true,
  defaultSearchType: 'JOB',
  hideFooterDivider: true,
  showDesktopSearch: true,
} satisfies LayoutHandleProps;

export function shouldRevalidate({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  const removedExploreTmpId =
    currentUrl.searchParams.has('exploreTmpId') &&
    !nextUrl.searchParams.has('exploreTmpId');

  if (removedExploreTmpId) return false;

  return defaultShouldRevalidate;
}

export async function loader({
  request,
  context,
}: HorizonAppRouteLoaderArgs<typeof salariesExploreRoute>) {
  const { exploreTmpId } = getAppRouteSearchParams({
    route: salariesExploreRoute,
    url: request.url,
  });

  const { user } = await apiIsomorphicFetchUserEnvironment({
    request,
    context,
  })();

  if (!user) {
    return {
      type: 'not-authenticated',
      overrideLocale: I18nLocale.EN,
    } as const satisfies LoaderDataWithOverrideLocale;
  }

  const [searchApiKey, compensationSurvey, recentSubmissions, tmpStorage] =
    await Promise.all([
      apiIsomorphicFetchSynonymSearchApiKey({ request, context })(),
      salaryApiIsomorphicFetchCompensationSurvey({ request, context })(),
      salaryApiIsomorphicFetchRecentSubmissions({ request, context })(),
      exploreTmpId
        ? salaryApiIsomorphicFetchTmpStorage({ request, context })(exploreTmpId)
        : null,
    ]);

  return {
    type: 'authenticated',
    searchApiKey,
    alreadySubmitted: compensationSurvey.alreadySubmitted,
    salaryModelCreated:
      Date.parse(compensationSurvey.salaryModelCreated) / 1000,
    recentSubmissions,
    overrideLocale: I18nLocale.EN,
    initialSurveyReportFormData: tmpStorage?.surveyReport ?? null,
  } as const satisfies LoaderDataWithOverrideLocale;
}

export default function SalaryExploreHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();
  const { appConfig } = useAppConfig();
  useTrackPageView('Explore Salaries');

  const [tmpId, setTmpId] = useQueryParamState({ name: 'tmpId' });
  useEffect(() => {
    if (tmpId && loaderData.initialSurveyReportFormData) {
      setTmpId(undefined, { replace: true });
      void apiDeleteTmpStorage(tmpId);
    }
  }, [tmpId, loaderData.initialSurveyReportFormData, setTmpId]);

  return (
    <RouteModule
      title={i18n('Explore Nonprofit Salaries')}
      ogImage={getStaticImageSrc(
        'compdb/og-image.png',
        appConfig.idealist.staticImgPath,
      )}
    >
      {loaderData.type === 'authenticated' ? (
        <SalaryExplorePage
          initialSearchApiKey={loaderData.searchApiKey}
          recentSubmissions={loaderData.recentSubmissions}
          salaryModelCreated={loaderData.salaryModelCreated}
          alreadySubmitted={loaderData.alreadySubmitted}
          initialSurveyReportFormData={loaderData.initialSurveyReportFormData}
        />
      ) : (
        <SalariesExplorePageAuth />
      )}
    </RouteModule>
  );
}
